import { useCreateBusiness } from '@/api';
import {
  Button,
  Checkbox,
  Input,
  FormSelect,
  Textarea,
} from '@/components/atoms';
import type { OutletContext } from '@/components/layouts';
import {
  FormField,
  ToggleGroup,
  ToggleGroupItem,
  BusinessAddressFields,
} from '@/components/molecules';
import { channel, sales, businessIndustries } from '@/lib/constants';
import { resolveBusinessAddressForApi } from '@/lib/helpers/business-address';
import {
  businessCreationSchema,
  type BusinessCreationFormData,
} from '@/validation-schema';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Controller,
  useForm,
  type Control,
  type FieldValues,
  type UseFormRegister,
  type UseFormWatch,
} from 'react-hook-form';
import { useOutletContext } from 'react-router';

const TERMS_URL = 'https://gosellyt.com/terms';
const PRIVACY_URL = 'https://gosellyt.com/privacy-policy';

interface BusinessProfileFormProps {
  userId?: number;
}

export const BusinessProfileForm = ({
  userId = 10,
}: BusinessProfileFormProps) => {
  const { incrementStep } = useOutletContext<OutletContext>();

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
    watch,
  } = useForm({
    resolver: yupResolver(businessCreationSchema),
    mode: 'onChange',
    defaultValues: {
      agreedToTerms: false,
      country: 'nigeria',
    },
  });

  const categoryValue = watch('category');

  const { mutate, isPending } = useCreateBusiness(incrementStep);

  const create = (payload: BusinessCreationFormData) => {
    if (!userId) return;
    const address = resolveBusinessAddressForApi(payload);
    mutate({
      userId,
      name: payload.name,
      category: payload.category,
      description: payload.description,
      orderVolume: payload.orderVolume,
      salesChannel: payload.salesChannel,
      ...address,
    });
  };

  return (
    <form
      onSubmit={handleSubmit(create)}
      className="grid grid-cols-2 gap-4 px-4 md:px-6 lg:px-8"
    >
      <FormField
        label="Business Name"
        className="max-md:col-span-2"
        error={errors.name?.message}
        required
      >
        <Input placeholder="e.g. Jane" {...register('name')} />
      </FormField>

      <FormField
        label="Business Category"
        className="max-md:col-span-2"
        error={errors.category?.message}
        required
      >
        <FormSelect
          placeholder="e.g. Don"
          value={categoryValue}
          onChange={(value) =>
            setValue('category', value, { shouldValidate: true })
          }
          options={businessIndustries}
        />
      </FormField>

      <FormField
        label="Business Description"
        className="col-span-2"
        error={errors.description?.message}
        required
        infoTip="Minimum 100 characters. This helps Sellyt AI understand your business and assist your customers better."
      >
        <Textarea
          placeholder="Tell us what your business does (at least 100 characters)"
          rows={4}
          {...register('description')}
        />
      </FormField>

      <FormField
        label="How many orders do you get weekly?"
        className="col-span-2"
        error={errors.orderVolume?.message}
        required
      >
        <Controller
          name="orderVolume"
          control={control}
          render={({ field }) => (
            <ToggleGroup
              type="single"
              value={field.value}
              onValueChange={(value) => field.onChange(value)}
              className="grid grid-cols-3 sm:grid-cols-5 gap-2"
            >
              {sales.map((sale) => (
                <ToggleGroupItem
                  key={sale}
                  value={sale}
                  className="rounded-md px-6 h-11 font-semibold cursor-pointer transition-colors duration-300"
                >
                  {sale}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          )}
        />
      </FormField>

      <BusinessAddressFields
        register={register as unknown as UseFormRegister<FieldValues>}
        control={control as unknown as Control<FieldValues>}
        errors={errors}
        watch={watch as unknown as UseFormWatch<FieldValues>}
        className="col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4"
      />

      <FormField
        label="Preferred Sales Channel"
        className="col-span-2"
        error={errors.salesChannel?.message}
        required
      >
        <Controller
          name="salesChannel"
          control={control}
          render={({ field }) => (
            <ToggleGroup
              type="single"
              value={field.value}
              onValueChange={(value) => field.onChange(value)}
              className="grid grid-cols-3 md:grid-cols-4 gap-2"
            >
              {channel.map((item) => (
                <ToggleGroupItem
                  key={item.value}
                  value={item.value}
                  className="rounded-md px-6 h-11 font-semibold cursor-pointer transition-colors duration-300"
                >
                  {item.label}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          )}
        />
      </FormField>

      <div className="col-span-2 flex flex-col gap-2">
        <Controller
          name="agreedToTerms"
          control={control}
          render={({ field }) => (
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox
                checked={field.value}
                onCheckedChange={(checked) => field.onChange(checked === true)}
                className="mt-0.5"
              />
              <span className="text-sm leading-relaxed">
                I agree to the{' '}
                <a
                  href={TERMS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline"
                >
                  General Terms of Use
                </a>
                ,{' '}
                <a
                  href={TERMS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline"
                >
                  Merchant Terms of Use
                </a>{' '}
                &amp;{' '}
                <a
                  href={PRIVACY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline"
                >
                  General Privacy policy
                </a>{' '}
                of Sellyt.
              </span>
            </label>
          )}
        />
        {errors.agreedToTerms?.message && (
          <span className="text-destructive text-xs">
            {errors.agreedToTerms.message}
          </span>
        )}
      </div>

      <Button
        type="submit"
        className="col-span-2"
        label="Finish"
        size="lg"
        isLoading={isPending}
        disabled={!isValid}
      />
    </form>
  );
};
